// Wave 14+ / Rapha Fit Track C — Catalog admin page.
//
// Lists every garment in the active catalog with inline edit fields
// for name, category, fit_offset_pct, try_on_color, coverage Y range,
// and the sizes JSON. Mutations hit POST/PATCH/DELETE /fit/catalog.
//
// MVP gating: role==='rapha' (Rapha staff log in as a rapha user).
// Future: add a dedicated 'admin' role / scope to fence this off from
// regular Rapha members.

import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Grid,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import SaveIcon from '@mui/icons-material/Save';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import AppHeader from '../components/AppHeader';
import { colors } from '../theme';
import { useCurrentUser } from '../services/useCurrentUser';

interface GarmentRow {
  id: string;
  brand: string;
  category: string;
  name: string;
  fitOffsetPct: number;
  sizes: Array<Record<string, number | string>>;
  tryOnColor: string | null;
  coverageYLoRatio: number | null;
  coverageYHiRatio: number | null;
  isActive: boolean;
}

const API = process.env.REACT_APP_API || 'https://api.aero.chat';
const TOKEN_KEY = process.env.REACT_APP_TOKEN_KEY || 'aero.chat_token';

function authHeaders(): HeadersInit {
  const token = localStorage.getItem(TOKEN_KEY) ?? '';
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  };
}

export default function RaphaCatalog() {
  const { t } = useTranslation('rapha-fit');
  const user = useCurrentUser();
  const navigate = useNavigate();
  const [rows, setRows] = useState<GarmentRow[]>([]);
  const [draft, setDraft] = useState<Record<string, Partial<GarmentRow>>>({});
  const [creating, setCreating] = useState(false);
  const [newRow, setNewRow] = useState<Partial<GarmentRow>>({
    id: '',
    category: 'jersey',
    name: '',
    fitOffsetPct: 0,
    sizes: [],
  });

  useEffect(() => {
    if (!user.isAuthenticated) return;
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user.isAuthenticated]);

  const refresh = () => {
    fetch(`${API}/fit/catalog`, { headers: authHeaders() })
      .then((r) => (r.ok ? r.json() : []))
      .then((data: any[]) => {
        // GET /fit/catalog returns the catalog garment shape used by sizing.ts,
        // not the DB row. Re-fetch each row via PATCH-friendly fields.
        // The DB shape is what we render; we'll use the catalog payload as-is.
        setRows(
          data.map((g) => ({
            id: g.id,
            brand: 'rapha',
            category: g.category,
            name: g.name,
            fitOffsetPct: g.fit_offset_pct ?? 0,
            sizes: g.sizes ?? [],
            tryOnColor: g.try_on_color ?? null,
            coverageYLoRatio: g.coverage_y_lo_ratio ?? null,
            coverageYHiRatio: g.coverage_y_hi_ratio ?? null,
            isActive: true,
          })),
        );
      })
      .catch(() => {});
  };

  // Gate: non-rapha users see the same card as /rapha/fit.
  if (user.isAuthenticated && user.role !== 'rapha') {
    return (
      <Box sx={{ minHeight: '100vh', backgroundColor: colors.bg }}>
        <AppHeader />
        <Box sx={{ maxWidth: 640, mx: 'auto', mt: 8, px: 3 }}>
          <Typography variant="h4" sx={{ fontWeight: 800, mb: 1 }}>
            {t('gate.title')}
          </Typography>
          <Typography sx={{ color: colors.textMuted, mb: 3 }}>{t('gate.body')}</Typography>
          <Button
            component={RouterLink}
            to="/session"
            variant="contained"
            sx={{
              backgroundColor: colors.brand,
              color: colors.bg,
              fontWeight: 700,
              '&:hover': { backgroundColor: colors.brandBright },
            }}
          >
            {t('gate.cta')}
          </Button>
        </Box>
      </Box>
    );
  }

  const updateDraft = (id: string, patch: Partial<GarmentRow>) => {
    setDraft((d) => ({ ...d, [id]: { ...d[id], ...patch } }));
  };

  const save = async (id: string) => {
    const patch = draft[id];
    if (!patch) return;
    const body: any = {};
    if (patch.name !== undefined) body.name = patch.name;
    if (patch.category !== undefined) body.category = patch.category;
    if (patch.fitOffsetPct !== undefined) body.fitOffsetPct = patch.fitOffsetPct;
    if (patch.tryOnColor !== undefined) body.tryOnColor = patch.tryOnColor;
    if (patch.coverageYLoRatio !== undefined) body.coverageYLoRatio = patch.coverageYLoRatio;
    if (patch.coverageYHiRatio !== undefined) body.coverageYHiRatio = patch.coverageYHiRatio;
    if (patch.sizes !== undefined) body.sizes = patch.sizes;
    await fetch(`${API}/fit/catalog/${id}`, {
      method: 'PATCH',
      headers: authHeaders(),
      body: JSON.stringify(body),
    });
    setDraft((d) => {
      const next = { ...d };
      delete next[id];
      return next;
    });
    refresh();
  };

  const del = async (id: string) => {
    await fetch(`${API}/fit/catalog/${id}`, { method: 'DELETE', headers: authHeaders() });
    refresh();
  };

  const create = async () => {
    if (!newRow.id || !newRow.name) return;
    await fetch(`${API}/fit/catalog`, {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify({
        id: newRow.id,
        brand: 'rapha',
        category: newRow.category ?? 'jersey',
        name: newRow.name,
        fitOffsetPct: newRow.fitOffsetPct ?? 0,
        sizes: newRow.sizes ?? [],
        tryOnColor: newRow.tryOnColor ?? null,
        coverageYLoRatio: newRow.coverageYLoRatio ?? null,
        coverageYHiRatio: newRow.coverageYHiRatio ?? null,
      }),
    });
    setCreating(false);
    setNewRow({ id: '', category: 'jersey', name: '', fitOffsetPct: 0, sizes: [] });
    refresh();
  };

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: colors.bg }}>
      <AppHeader />
      <Box sx={{ maxWidth: 1240, mx: 'auto', px: { xs: 2.5, md: 4 }, py: { xs: 4, md: 6 } }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 4 }} flexWrap="wrap" gap={2}>
          <Box>
            <Typography variant="overline" sx={{ color: colors.brand }}>
              {t('catalog.overline')}
            </Typography>
            <Typography variant="h3" sx={{ fontSize: { xs: '1.75rem', md: '2.5rem' }, fontWeight: 800, mt: 1 }}>
              {t('catalog.title')}
            </Typography>
            <Typography sx={{ color: colors.textMuted, mt: 1 }}>{t('catalog.subtitle')}</Typography>
          </Box>
          <Button
            onClick={() => setCreating(true)}
            startIcon={<AddIcon />}
            variant="contained"
            sx={{
              backgroundColor: colors.brand,
              color: colors.bg,
              fontWeight: 700,
              '&:hover': { backgroundColor: colors.brandBright },
            }}
          >
            {t('catalog.add')}
          </Button>
        </Stack>

        {creating && (
          <CatalogRowEditor
            row={newRow as GarmentRow}
            isNew
            onChange={(patch) => setNewRow((r) => ({ ...r, ...patch }))}
            onSave={create}
            onCancel={() => setCreating(false)}
            t={t}
          />
        )}

        <Stack spacing={2}>
          {rows.map((row) => {
            const patched = { ...row, ...draft[row.id] };
            const dirty = !!draft[row.id];
            return (
              <CatalogRowEditor
                key={row.id}
                row={patched}
                isNew={false}
                dirty={dirty}
                onChange={(patch) => updateDraft(row.id, patch)}
                onSave={() => save(row.id)}
                onDelete={() => del(row.id)}
                t={t}
              />
            );
          })}
          {rows.length === 0 && (
            <Typography sx={{ color: colors.textMuted, fontStyle: 'italic' }}>
              {t('catalog.empty')}
            </Typography>
          )}
        </Stack>
      </Box>
    </Box>
  );
}

function CatalogRowEditor({
  row,
  isNew,
  dirty,
  onChange,
  onSave,
  onDelete,
  onCancel,
  t,
}: {
  row: GarmentRow;
  isNew: boolean;
  dirty?: boolean;
  onChange: (patch: Partial<GarmentRow>) => void;
  onSave: () => void;
  onDelete?: () => void;
  onCancel?: () => void;
  t: any;
}) {
  const [sizesText, setSizesText] = useState(() => JSON.stringify(row.sizes ?? [], null, 2));
  const sizesValid = useMemo(() => {
    try {
      const parsed = JSON.parse(sizesText);
      return Array.isArray(parsed);
    } catch {
      return false;
    }
  }, [sizesText]);

  const onSizesBlur = () => {
    if (!sizesValid) return;
    try {
      onChange({ sizes: JSON.parse(sizesText) });
    } catch {}
  };

  return (
    <Box
      sx={{
        p: 3,
        borderRadius: 3,
        backgroundColor: colors.surface,
        border: `1px solid ${dirty ? colors.borderFocus : colors.border}`,
      }}
    >
      <Grid container spacing={2}>
        <Grid item xs={12} md={3}>
          <TextField
            label={t('catalog.field.id')}
            value={row.id}
            onChange={(e) => isNew && onChange({ id: e.target.value })}
            disabled={!isNew}
            size="small"
            fullWidth
          />
        </Grid>
        <Grid item xs={12} md={5}>
          <TextField
            label={t('catalog.field.name')}
            value={row.name ?? ''}
            onChange={(e) => onChange({ name: e.target.value })}
            size="small"
            fullWidth
          />
        </Grid>
        <Grid item xs={6} md={2}>
          <TextField
            label={t('catalog.field.category')}
            value={row.category ?? ''}
            onChange={(e) => onChange({ category: e.target.value })}
            size="small"
            fullWidth
          />
        </Grid>
        <Grid item xs={6} md={2}>
          <TextField
            label={t('catalog.field.fitOffsetPct')}
            type="number"
            inputProps={{ step: 0.01 }}
            value={row.fitOffsetPct ?? 0}
            onChange={(e) => onChange({ fitOffsetPct: parseFloat(e.target.value) })}
            size="small"
            fullWidth
          />
        </Grid>
        <Grid item xs={4} md={2}>
          <TextField
            label={t('catalog.field.tryOnColor')}
            value={row.tryOnColor ?? ''}
            onChange={(e) => onChange({ tryOnColor: e.target.value })}
            size="small"
            fullWidth
            placeholder="#ff007a"
          />
        </Grid>
        <Grid item xs={4} md={2}>
          <TextField
            label={t('catalog.field.yLo')}
            type="number"
            inputProps={{ step: 0.01 }}
            value={row.coverageYLoRatio ?? ''}
            onChange={(e) => onChange({ coverageYLoRatio: e.target.value === '' ? null : parseFloat(e.target.value) })}
            size="small"
            fullWidth
          />
        </Grid>
        <Grid item xs={4} md={2}>
          <TextField
            label={t('catalog.field.yHi')}
            type="number"
            inputProps={{ step: 0.01 }}
            value={row.coverageYHiRatio ?? ''}
            onChange={(e) => onChange({ coverageYHiRatio: e.target.value === '' ? null : parseFloat(e.target.value) })}
            size="small"
            fullWidth
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            label={t('catalog.field.sizes')}
            multiline
            minRows={3}
            value={sizesText}
            onChange={(e) => setSizesText(e.target.value)}
            onBlur={onSizesBlur}
            size="small"
            fullWidth
            error={!sizesValid}
            helperText={!sizesValid ? t('catalog.field.sizesInvalid') : t('catalog.field.sizesHelp')}
            sx={{ '& textarea': { fontFamily: '"JetBrains Mono", monospace', fontSize: '0.8125rem' } }}
          />
        </Grid>
      </Grid>
      <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
        <Button
          onClick={onSave}
          startIcon={<SaveIcon />}
          variant="contained"
          size="small"
          disabled={!isNew && !dirty}
          sx={{
            backgroundColor: colors.brand,
            color: colors.bg,
            '&:hover': { backgroundColor: colors.brandBright },
          }}
        >
          {isNew ? t('catalog.create') : t('catalog.save')}
        </Button>
        {onCancel && (
          <Button onClick={onCancel} variant="outlined" size="small" sx={{ color: colors.textMuted, borderColor: colors.border }}>
            {t('catalog.cancel')}
          </Button>
        )}
        {onDelete && (
          <IconButton onClick={onDelete} size="small" sx={{ color: colors.danger, ml: 'auto' }}>
            <DeleteOutlineIcon />
          </IconButton>
        )}
        {dirty && !isNew && <Chip label={t('catalog.unsaved')} size="small" sx={{ ml: 'auto', backgroundColor: 'transparent', border: `1px solid ${colors.warning}`, color: colors.warning }} />}
      </Stack>
    </Box>
  );
}
